import { useEffect, useState } from 'react';
import { HERO, HEROES, RANKS, rankFor, TITLES } from '@aleaces/engine';
import { Portrait } from '../components/Portrait';
import { redeemCode, setName } from '../actions';
import { useProfile, useMuted, useMusicVolume, useSfxVolume } from '../hooks';
import { levelOf } from '../profile';
import { setMusicVolume, setSfxVolume, toggleMuted } from '../settings';
import { clearRecoveryPending, isRecoveryPending, supabase } from '../supabase';
import { toast } from '../toast';

const authInputStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,.35)', border: '1px solid var(--panel-line)', color: 'var(--parch)',
  borderRadius: 6, padding: '8px 12px', fontFamily: 'var(--font-ui)', fontSize: 15, width: 240,
};

/** Maps Supabase auth errors to German user-facing messages. */
function authErrorText(msg: string): string {
  if (/invalid login credentials/i.test(msg)) return 'E-Mail oder Passwort ist falsch.';
  if (/already registered/i.test(msg)) return 'Diese E-Mail ist bereits registriert – melde dich an.';
  if (/password should be/i.test(msg)) return 'Das Passwort braucht mindestens 6 Zeichen.';
  if (/rate limit/i.test(msg)) return 'Zu viele Versuche – warte kurz und probiere es dann erneut.';
  if (/not confirmed/i.test(msg)) return 'Bestätige zuerst deine E-Mail über den Link im Postfach.';
  return msg;
}

type AuthMode = 'signin' | 'signup' | 'reset';

/** Account login via Supabase (email + password, with reset and a magic-link
 * fallback): links the anonymous progress to a real account so it survives
 * device changes. Rendered only when Supabase env vars are configured. */
function AccountPanel() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<AuthMode>('signin');
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [changingPassword, setChangingPassword] = useState(() => isRecoveryPending());

  useEffect(() => {
    if (!supabase) return;
    void supabase.auth.getSession().then(({ data }) => setSessionEmail(data.session?.user.email ?? null));
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      setSessionEmail(session?.user.email ?? null);
      if (event === 'PASSWORD_RECOVERY') setChangingPassword(true);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!supabase || busy) return;
    const mail = email.trim();
    if (!mail) return;
    setBusy(true);
    setInfo(null);
    try {
      if (mode === 'signin') {
        const { error } = await supabase.auth.signInWithPassword({ email: mail, password });
        if (error) toast('Anmeldung fehlgeschlagen', authErrorText(error.message));
      } else if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({
          email: mail, password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) toast('Registrierung fehlgeschlagen', authErrorText(error.message));
        else if (!data.session) setInfo('Fast geschafft: Bestätige deine E-Mail über den Link im Postfach und melde dich danach hier an.');
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(mail, { redirectTo: window.location.origin });
        if (error) toast('Zurücksetzen fehlgeschlagen', authErrorText(error.message));
        else setInfo('E-Mail verschickt: Über den Link kommst du zurück und kannst hier ein neues Passwort setzen.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function sendMagicLink() {
    if (!supabase || busy) return;
    const mail = email.trim();
    if (!mail) {
      toast('E-Mail fehlt', 'Trag zuerst deine E-Mail-Adresse ein.');
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.signInWithOtp({ email: mail, options: { emailRedirectTo: window.location.origin } });
    setBusy(false);
    if (error) toast('Anmeldung fehlgeschlagen', authErrorText(error.message));
    else setInfo('Magic-Link verschickt – öffne den Link im selben Browser, in dem das Spiel läuft.');
  }

  async function savePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!supabase || busy) return;
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) {
      toast('Passwort nicht gespeichert', authErrorText(error.message));
      return;
    }
    clearRecoveryPending();
    setChangingPassword(false);
    setPassword('');
    toast('Passwort gespeichert', 'Ab jetzt kannst du dich überall mit E-Mail und Passwort anmelden.');
  }

  const ui: React.CSSProperties = { fontFamily: 'var(--font-ui)', fontSize: 14 };

  if (sessionEmail) {
    return (
      <div className="panel">
        <div className="eyebrow" style={{ marginBottom: 10 }}>Konto</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', ...ui }}>
          <span>Angemeldet als <b style={{ color: 'var(--gold-hi)' }}>{sessionEmail}</b></span>
          <button className="btn sm ghost" onClick={() => void supabase?.auth.signOut()}>Abmelden</button>
          {!changingPassword && (
            <button className="btn sm ghost" onClick={() => setChangingPassword(true)}>Passwort ändern</button>
          )}
          <span className="muted" style={{ fontSize: 12 }}>
            Dein Fortschritt hängt an diesem Konto und ist auf jedem Gerät verfügbar.
          </span>
        </div>
        {changingPassword && (
          <form onSubmit={(e) => void savePassword(e)} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              placeholder="Neues Passwort (min. 6 Zeichen)"
              aria-label="Neues Passwort"
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
              style={authInputStyle}
            />
            <button className="btn sm gold" type="submit" disabled={busy}>{busy ? 'Speichere …' : 'Passwort speichern'}</button>
            <button className="btn sm ghost" type="button" onClick={() => { setChangingPassword(false); clearRecoveryPending(); }}>Abbrechen</button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="eyebrow" style={{ marginBottom: 10 }}>Konto</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <button className={'btn sm' + (mode === 'signin' ? ' gold' : ' ghost')} onClick={() => { setMode('signin'); setInfo(null); }}>Anmelden</button>
        <button className={'btn sm' + (mode === 'signup' ? ' gold' : ' ghost')} onClick={() => { setMode('signup'); setInfo(null); }}>Registrieren</button>
      </div>
      <form onSubmit={(e) => void submit(e)} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="email"
          required
          value={email}
          placeholder="deine@mail.de"
          aria-label="E-Mail-Adresse"
          autoComplete="email"
          onChange={(e) => setEmail(e.target.value)}
          style={authInputStyle}
        />
        {mode !== 'reset' && (
          <input
            type="password"
            required
            minLength={6}
            value={password}
            placeholder={mode === 'signup' ? 'Passwort (min. 6 Zeichen)' : 'Passwort'}
            aria-label="Passwort"
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            onChange={(e) => setPassword(e.target.value)}
            style={authInputStyle}
          />
        )}
        <button className="btn sm gold" type="submit" disabled={busy}>
          {busy ? 'Sende …' : mode === 'signin' ? 'Anmelden' : mode === 'signup' ? 'Konto erstellen' : 'Reset-Mail senden'}
        </button>
      </form>
      {info && <p className="muted" style={{ margin: '10px 0 0', ...ui }}>{info}</p>}
      <div style={{ display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap', ...ui, fontSize: 12 }}>
        {mode !== 'reset' ? (
          <button className="linklike" type="button" onClick={() => { setMode('reset'); setInfo(null); }}>Passwort vergessen?</button>
        ) : (
          <button className="linklike" type="button" onClick={() => { setMode('signin'); setInfo(null); }}>Zurück zur Anmeldung</button>
        )}
        <button className="linklike" type="button" onClick={() => void sendMagicLink()}>Ohne Passwort per Magic-Link</button>
        <span className="muted">Beim ersten Login wird dein bisheriger Fortschritt mit dem Konto verknüpft.</span>
      </div>
    </div>
  );
}

export function ProfileScreen() {
  const p = useProfile();
  const muted = useMuted();
  const sfxVol = useSfxVolume();
  const musicVol = useMusicVolume();
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [codeDraft, setCodeDraft] = useState('');
  if (!p) return null;
  const s = p.stats;
  const rk = rankFor(p.rating);
  const winrate = s.hands ? Math.round((s.handsWon / s.hands) * 100) : 0;
  const hero = HERO[p.hero] ?? HEROES[0]!;

  return (
    <section className="screen">
      <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gap: 18 }}>
        <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
          <Portrait heroId={p.hero} skinId={p.skins[p.hero] ?? 'base'} frame={p.frame} size={110} />
          <div>
            <div className="eyebrow">{(TITLES.find((t) => t.id === p.title) ?? TITLES[0]!).name}</div>
            <h1>{p.name}</h1>
            <div className="muted" style={{ fontFamily: 'var(--font-ui)' }}>
              Lieblingsheld {hero.name} · Stufe {levelOf(p.xp)} · {rk.name} ({p.rating}) · Bestwert {p.peakRating}
            </div>
          </div>
        </div>
        <div className="panel">
          <div className="eyebrow" style={{ marginBottom: 10 }}>Anzeigename</div>
          <input
            value={nameDraft ?? p.name}
            maxLength={24}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={() => {
              const v = (nameDraft ?? '').trim();
              if (v && v !== p.name) setName(v);
              setNameDraft(null);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            style={{
              background: 'rgba(0,0,0,.35)', border: '1px solid var(--panel-line)', color: 'var(--parch)',
              borderRadius: 6, padding: '8px 12px', fontFamily: 'var(--font-ui)', fontSize: 15, width: 280,
            }}
          />
          <span className="muted" style={{ fontFamily: 'var(--font-ui)', fontSize: 12, marginLeft: 10 }}>
            So sehen dich andere Spieler am Tisch (Enter zum Speichern).
          </span>
        </div>
        <div className="panel">
          <div className="eyebrow" style={{ marginBottom: 10 }}>Saisonstatistik</div>
          <div className="stat-grid">
            <div><span>Hände gespielt</span><b>{s.hands}</b></div>
            <div><span>Hände gewonnen</span><b>{s.handsWon} ({winrate} %)</b></div>
            <div><span>Showdowns gewonnen</span><b>{s.showdownsWon}</b></div>
            <div><span>Größter Pot</span><b>{s.biggestPot}</b></div>
            <div><span>Turniere</span><b>{s.tournaments}</b></div>
            <div><span>Turniersiege</span><b>{s.wins}</b></div>
            <div><span>Top-3-Platzierungen</span><b>{s.top3}</b></div>
            <div><span>Bestes Blatt</span><b>{s.bestHand}</b></div>
            <div><span>Emotes gesendet</span><b>{s.emotes}</b></div>
            <div><span>Gesamt-EP</span><b>{p.xp}</b></div>
          </div>
        </div>
        <div className="panel">
          <div className="eyebrow" style={{ marginBottom: 10 }}>Ränge dieser Saison</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {RANKS.map((r) => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-ui)', fontSize: 13, opacity: p.rating >= r.min ? 1 : 0.5 }}>
                <div className="rank-shield" style={{ background: r.color, width: 34, height: 38, fontSize: 14 }}>{r.name[0]}</div>
                {r.name}<span className="muted">ab {r.min}</span>
              </div>
            ))}
          </div>
          <p className="muted" style={{ fontFamily: 'var(--font-ui)', fontSize: 13, margin: '12px 0 0' }}>
            Wertung: 6-handiges Turnier – 1. +45, 2. +25, 3. +10, 4. −10, 5. −20, 6. −30. Knapp über einer Rangschwelle kann man
            nicht unter die Schwelle fallen (Rangschutz). Diamant schaltet den Kartenrücken „Goldfiligran“ frei, Legende den
            Drachenrahmen und den Titel „Tavernenlegende“.
          </p>
        </div>
        {supabase && <AccountPanel />}
        <div className="panel">
          <div className="eyebrow" style={{ marginBottom: 10 }}>Einlösecode</div>
          <form
            style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
            onSubmit={(e) => {
              e.preventDefault();
              const code = codeDraft.trim();
              if (!code) return;
              redeemCode(code);
              setCodeDraft('');
            }}
          >
            <input
              value={codeDraft}
              maxLength={32}
              placeholder="z. B. BETA2026"
              aria-label="Einlösecode"
              onChange={(e) => setCodeDraft(e.target.value.toUpperCase())}
              style={{
                background: 'rgba(0,0,0,.35)', border: '1px solid var(--panel-line)', color: 'var(--parch)',
                borderRadius: 6, padding: '8px 12px', fontFamily: 'var(--font-ui)', fontSize: 15, width: 220,
                textTransform: 'uppercase', letterSpacing: '0.08em',
              }}
            />
            <button className="btn sm gold" type="submit" disabled={codeDraft.trim().length < 3}>Einlösen</button>
            <span className="muted" style={{ fontFamily: 'var(--font-ui)', fontSize: 12 }}>
              Codes aus Aktionen und der Beta schalten Kosmetik oder Taler frei.
            </span>
          </form>
        </div>
        <div className="panel">
          <div className="eyebrow" style={{ marginBottom: 10 }}>Einstellungen</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontFamily: 'var(--font-ui)', fontSize: 14 }}>
            <button className="btn sm ghost" onClick={() => toggleMuted()}>
              {muted ? '🔇 Stumm – einschalten' : '🔊 Ton an'}
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Effekte
              <input
                type="range" min={0} max={100} value={sfxVol}
                onChange={(e) => setSfxVolume(+e.target.value)}
                style={{ width: 140, accentColor: 'var(--gold)' }}
              />
              <b style={{ minWidth: 32, fontVariantNumeric: 'tabular-nums' }}>{sfxVol}</b>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Musik
              <input
                type="range" min={0} max={100} value={musicVol}
                onChange={(e) => setMusicVolume(+e.target.value)}
                style={{ width: 140, accentColor: 'var(--gold)' }}
              />
              <b style={{ minWidth: 32, fontVariantNumeric: 'tabular-nums' }}>{musicVol}</b>
            </label>
            <span className="muted" style={{ fontSize: 12 }}>
              Dein Fortschritt liegt serverseitig auf deinem Konto und ist auf diesem Gerät automatisch verknüpft.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
